import {createSelector} from 'reselect';
import assign from 'lodash/assign';
import {selectSearchTerm} from '.';
import _ from 'lodash';

const selectFeatures = state => state.features;

const selectTransientFeatureGroups = state => selectFeatures(state).transientFeatureGroups;

const selectMasterFeatureGroups = state => selectFeatures(state).masterFeatureGroups;

const selectFabricatorInclusionPreset = state => state.featureInclusion.fabricatorInclusionPreset;

const passedArgument = (state, feature) => feature;

export const getFilteredFeatureGroups = createSelector(
  [selectMasterFeatureGroups, selectTransientFeatureGroups, selectSearchTerm, selectFabricatorInclusionPreset],
  (masterFeatureGroups, featureGroups, term, preset) => {
    const groups = _.unionBy(featureGroups, masterFeatureGroups, "name");
    return groups.filter(fg => {
        let filter = !term || fg.name.toLowerCase().indexOf(term) >= 0;
        if (preset && preset.featureGroups) {
          let allowedFeatureGroup = false;
          for (let i = 0; i < preset.featureGroups.length; i++) {
            allowedFeatureGroup |= _.isEqual(fg.name.toLowerCase(), preset.featureGroups[i].featureGroup.toLowerCase());
          }
          filter &= allowedFeatureGroup;
        }
        return filter;
      }
    );
  }
);

const getActiveFeatureGroupName = (state) => selectFeatures(state).activeFeatureGroupName;

const selectTransientFeatures = (state) => selectFeatures(state).transientFeatures;

const selectMasterFeatures = (state) => selectFeatures(state).masterFeatures;

export const getMasterFeature = createSelector(
  [selectMasterFeatures, passedArgument],
  (masterFeatures, feature) => {
    let results = masterFeatures.filter((f) => {
      return f.id === feature.id;
    });
    if(results.length > 0) {
      return results[0];
    } else {
      return {};
    }
  }
);

export const getActiveFeatureGroup = createSelector(
  [selectMasterFeatureGroups, selectTransientFeatureGroups, getActiveFeatureGroupName, selectTransientFeatures, selectMasterFeatures],
  (masterFeatureGroups, featureGroups, activeFeatureGroupName, transientFeatures, masterFeatures) => {
    const groups = _.unionBy(featureGroups, masterFeatureGroups, "name");
    _.each(groups, function(group) {
      const masterGroup = _.find(masterFeatureGroups, function (mfg) {
        return mfg.name === group.name;
      });
      if(masterGroup)
        group.features = _.union(group.features, masterGroup.features);
    });
    //var features = mergeFeatures(transientFeatures, masterFeatures);
    let features = _.unionBy(transientFeatures, masterFeatures, "id");
    features = mergeFeatures(features);

    const featureGroup = groups.find(fg => fg.name === activeFeatureGroupName) || {};
    const activeFeatureGroup = assign({}, { features: [] }, featureGroup);
    return activeFeatureGroup.features.map(id => features.find(f => f.id === id || (_.findIndex(f.backingFeatures, bf => bf.id === id) >= 0)));
  }
);

function mergeFeatures(features) {
  let merged = [];
  features.sort((a, b) => {
    if (a.groupName < b.groupName)
      return -1;
    if (a.groupName > b.groupName)
      return 1;
    if (a.displayName < b.displayName)
      return -1;
    if (a.displayName > b.displayName)
      return 1;
    return 0;
  });

  merged.push(features[0]);
  for(let i = 1; i < features.length; i++) {
    if(features[i].displayName === features[i-1].displayName) {
      features[i - 1].backingFeatures = _.unionBy(features[i - 1].backingFeatures, features[i].backingFeatures, "id");
    } else {
      merged.push(features[i]);
    }
  }
  features = merged;
  return features;
}

export const getActiveFeatureImageStyle = createSelector(
  getActiveFeatureGroupName,
  (activeFeatureGroupName) => {
    const groupName = activeFeatureGroupName.toLowerCase();
    const round = /edge[s]*\s*profile/i.test(groupName) || /faucet[s]*/i.test(groupName);
    return round ? 'round' : 'rectangular';
  }
);

export const getActiveFeatureImageStyleStateless = _.memoize(groupName => {
  const round = /edge[s]*\s*profile/i.test(groupName) || /faucet[s]*/i.test(groupName);
  return round ? 'round' : 'rectangular';
});

export const getFilteredActiveFeatures = createSelector(
  [getActiveFeatureGroup, selectSearchTerm, selectFabricatorInclusionPreset],
  (activeFeatureGroup, term, preset) => {
    term = term.toLowerCase();
    return (term || (preset && preset.features)) ?
      activeFeatureGroup.filter(f => {
        let filter = (!term ||
          f.displayName.toLowerCase().indexOf(term) > -1 ||
          f.backingFeatures[0].aliases && f.backingFeatures[0].aliases.filter(a => a.toLowerCase().indexOf(term) > -1).length > 0
        );
        if(filter) {
          let found = false;
          for(let i = 0; i < preset.features.length; i++) {
            let pf = preset.features[i];
            if(_.isEqual(pf.featureDefinition_id, f.id)) {
              found = true;
            }
          }
          filter &= found;
        }
        return filter;
      })
      : activeFeatureGroup;
  }
);

export const getEnabledActiveFeatures = createSelector(
  getFilteredActiveFeatures,
  (filteredActiveFeatures) => filteredActiveFeatures.filter(f => (typeof f.enabled !== 'undefined' && f.enabled) || (typeof f.enabled === 'undefined' && !f.hideFromFrontEnd))
);

export const getDisabledActiveFeatures = createSelector(
  getFilteredActiveFeatures,
  (filteredActiveFeatures) => filteredActiveFeatures.filter(f => (typeof f.enabled !== 'undefined' && !f.enabled) || (typeof f.enabled === 'undefined' && f.hideFromFrontEnd))
);

export const hasFabricatorItems = createSelector(
  [selectFeatures, passedArgument],
  (features, featureGroup) => {
    const existingTF = features.transientFeatures.filter(tf => featureGroup.features.find(f => f === tf.id));
    return existingTF.length > 0;
  }
);
